// Hook wrapping the browser's `beforeinstallprompt` event so the SPA can
// show its own "Install app" button instead of relying on Chrome's
// auto-popup (which is intrusive and fires inconsistently).
//
// Behaviour:
//   * On supported browsers (Chrome / Edge / desktop Safari), the event
//     fires once when install criteria are met. We capture and stash it.
//   * `canInstall` is true while we hold a stashed event. The component
//     calling `prompt()` triggers Chrome's native install dialog and
//     resolves with the user's choice. After that the event is consumed —
//     `canInstall` flips back to false either way.
//   * On iOS Safari the event NEVER fires. Detected separately via
//     window.navigator.standalone — we surface a "How to install on iOS"
//     hint instead of a button.
//   * If the SPA is already installed (display-mode: standalone), we
//     return canInstall=false + alreadyInstalled=true so the UI hides
//     the button entirely.

import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallPromptState {
  canInstall: boolean;
  alreadyInstalled: boolean;
  isIos: boolean;
  /** Trigger the native install dialog. No-op if !canInstall. */
  prompt: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

function isStandaloneNow(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari: navigator.standalone is true once installed to home screen.
  // (Non-standard property; type lib doesn't include it.)
  if ((window.navigator as unknown as { standalone?: boolean }).standalone) {
    return true;
  }
  return false;
}

function detectIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Modern iPad reports as Macintosh+TouchPoints; cover both.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1)
  );
}

export function useInstallPrompt(): InstallPromptState {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [alreadyInstalled, setAlreadyInstalled] = useState(false);

  useEffect(() => {
    setAlreadyInstalled(isStandaloneNow());
    function onPrompt(e: Event) {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    }
    function onInstalled() {
      setAlreadyInstalled(true);
      setEvent(null);
    }
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  return {
    canInstall: !!event && !alreadyInstalled,
    alreadyInstalled,
    isIos: detectIos(),
    async prompt() {
      if (!event) return 'unavailable';
      await event.prompt();
      const choice = await event.userChoice;
      // Chrome won't fire beforeinstallprompt again for this session.
      setEvent(null);
      return choice.outcome;
    },
  };
}
